import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary, FormGroup } from "@mui/material";
import { ButtonArrow } from "@thrivetribe/mvf.assets.icons.button-arrow";
import { Button } from "@thrivetribe/mvf.ui.atoms.button";
import { FormikSelect } from "@thrivetribe/mvf.ui.atoms.formik-select";
import { LoadingSpinner } from "@thrivetribe/mvf.ui.molecules.loading-spinner";
import {
  OnboardingPage,
  OnboardingPageBody,
  OnboardingPageContent,
  OnboardingPageCopy,
  OnboardingPageFooter,
} from "@thrivetribe/mvf.ui.organisms.onboarding-page";
import { Form as FormikForm, Formik } from "formik";
import { signupNavLink } from "Onboarding/utils";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { EthnicityValidationSchema } from "../OnboardingValidations";
import "./EthnicitySelection.scss";
import { useGetUserQuery, useUpdateUserMutation } from "redux/userApiSlice";
import { ethnicityOptions } from "enums/Ethnicity";
import { useUser } from "components/user/UserContext";
import { Gtag } from "configs/ReactGa";
import { ReactGa } from "ui/atoms/ReactGa";
import { ImagesUrl } from "configs/EnvironmentConfig";

const EthnicitySelection = () => {
  let navigate = useNavigate();
  let user = useUser();
  let { data: userObj, isLoading } = useGetUserQuery(user?.userId);
  let [updateProfile] = useUpdateUserMutation({
    fixedCacheKey: user?.userId,
  });

  const updateProfileData = async (ethnicity: string) => {
    if (userObj) {
      const updatedProfile = { ...userObj, ethnicity };
      await updateProfile(updatedProfile)
        .unwrap()
        .then(() => {
          Gtag("event", "ethnicity_set", {
            user_id: user.userId,
            event_category: "account setup",
          });
          navigate("/portal-setup/emergency-contact");
        });
    }
  };

  const handleSubmit = async (values: any) => {
    updateProfileData(values.ethnicity.value);
  };

  const handleBack = () => {
    if (user.isLegacy && user.user?.personalDetails?.dateOfBirth) {
      navigate("/portal-setup");
    } else {
      navigate("/portal-setup/age");
    }
  };

  if (isLoading) {
    return <LoadingSpinner fullscreen={true} />;
  }

  return (
    <div className="ethnicity">
      <Formik
        initialValues={{ ethnicity: ethnicityOptions.filter((o) => o.value === userObj?.ethnicity)[0] }}
        onSubmit={handleSubmit}
        validationSchema={EthnicityValidationSchema}
        validateOnChange={true}
        validateOnBlur={true}
      >
        <FormikForm>
          <Helmet>
            <title>Set up - Ethnicity</title>
          </Helmet>
          <OnboardingPage
            user={user.displayName}
            links={signupNavLink}
            image={`${ImagesUrl}/yellow-shirt.webp`}
            title={"Set up"}
            onBackClick={handleBack}
          >
            <OnboardingPageContent hasStickyFooter centralizeContent>
              <OnboardingPageBody>
                <OnboardingPageCopy>
                  <h1>What's your ethnicity?</h1>
                </OnboardingPageCopy>
                <FormGroup className="onboarding-page__content--group">
                  <FormikSelect label="Select an option" name="ethnicity" options={ethnicityOptions} />
                </FormGroup>
                <Accordion square={true} elevation={0} className="onboarding-page__accordion">
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon fontSize="large" />}
                    aria-controls={`question-explanation`}
                  >
                    <span>None of our business?! Here’s why we ask.</span>
                  </AccordionSummary>
                  <AccordionDetails>
                    <span>
                      Looks like we didn't catch this when you first signed up. Why do we need to know? We're always
                      trying to up our game and be as inclusive as we can so knowing this helps us support you. Want to
                      keep it to yourself? Just select 'Prefer not to say'.
                    </span>
                  </AccordionDetails>
                </Accordion>
              </OnboardingPageBody>
              <OnboardingPageFooter>
                <Button type="submit" variant="primary" color="primary">
                  Continue
                  <ButtonArrow />
                </Button>
              </OnboardingPageFooter>
            </OnboardingPageContent>
          </OnboardingPage>
        </FormikForm>
      </Formik>
      <ReactGa
        eventName="ethnicity_requested"
        eventParams={{ user_id: user.userId, event_category: "account setup" }}
      />
    </div>
  );
};

export default EthnicitySelection;
